/**
 * PDP 变体选择器 v2 — Shopify-native 通用渲染
 *
 * 原则:
 *   - product.options 给几维就渲染几个 picker
 *   - 不解析 option1 字符串,不靠 option name 猜语义
 *   - 每个 picker 的样式由数据形态决定:
 *       该 option 各 value 都有不同的 featured_image  → 图片卡片网格
 *       该 option 各 value 共用图(或无图)             → 文字按钮网格
 *   - 长列表(>6 values)统一收起到 3 + 「他のN件を見る」
 */
(function(){
  const LS_LAST = 'vp2-last-pick';     // localStorage,跨产品记忆用户上次选过的"机型类"option

  function imgWidth(src, w){
    if(!src) return '';
    return src.replace(/(\?|&)width=\d+/g, '') + (src.includes('?')?'&':'?') + 'width=' + w;
  }

  function getOptionValue(variant, idx){
    return [variant.option1, variant.option2, variant.option3][idx];
  }

  function isTextOnlyOptionName(name){
    return /サイズ|尺码|尺寸|容量|着丈|股下|(^|[^a-z])size([^a-z]|$)|capacity|length/i.test(String(name || '').trim());
  }

  /** 该 option 是否需要图片渲染:>=60% values 有不同的 featured_image 即用图片模式 */
  function detectImageMode(product, optionIdx){
    const opt = product.options[optionIdx];
    if(!opt) return false;
    const vals = opt.values || [];
    if(vals.length < 2 || isTextOnlyOptionName(opt.name)) return false;
    const imgs = vals.map(val => {
      const v = product.variants.find(x => getOptionValue(x, optionIdx) === val);
      return v && v.thumb ? v.thumb : null;
    });
    if (String(opt.name || '').trim() === 'カラー・バンド長さ') return imgs.some(Boolean);
    const uniqueImgs = new Set(imgs.filter(Boolean));
    return uniqueImgs.size >= Math.ceil(vals.length * 0.6);
  }

  class VariantPickerV2 extends HTMLElement {
    connectedCallback(){
      const sectionId = this.dataset.sectionId;
      const dataEl = document.getElementById('vp2-data-' + sectionId);
      if(!dataEl){ console.warn('[vp2] data island not found'); return; }
      try { this.product = JSON.parse(dataEl.textContent); }
      catch(e){ console.error('[vp2] data parse failed', e); return; }

      // 当前选中
      const params = new URLSearchParams(location.search);
      const initId = +params.get('variant') || this.product.currentVariantId;
      this.current = this.product.variants.find(v => v.id === initId) || this.product.variants[0];
      if(!this.current) return;

      // 每个 option 是否用图片模式
      this.imageMode = this.product.options.map((_, i) => detectImageMode(this.product, i));

      // 折叠展开状态:每个 option 是否展开
      this.expanded = this.product.options.map(() => false);
      // 若当前选中的值不在前 3,自动展开该 option
      this.product.options.forEach((opt, i) => {
        const cur = getOptionValue(this.current, i);
        const idx = (opt.values || []).indexOf(cur);
        if(idx >= 3) this.expanded[i] = true;
      });
      // localStorage 跨产品记忆:若上次选过的某 option value 在本商品里,自动展开
      try {
        const last = JSON.parse(localStorage.getItem(LS_LAST) || 'null');
        if(last) Object.entries(last).forEach(([name, val]) => {
          const i = this.product.options.findIndex(o => o.name === name);
          if(i >= 0){
            const idx = (this.product.options[i].values || []).indexOf(val);
            if(idx >= 3) this.expanded[i] = true;
          }
        });
      } catch(e){}

      this.render();
      this.dispatchVariantChange();
    }

    /* 找最佳 variant:优先保持其他 option 当前值,缺货次选,再 fallback */
    findBestVariant(optionIdx, value){
      const cands = this.product.variants.filter(v => getOptionValue(v, optionIdx) === value);
      if(!cands.length) return null;
      const cur = this.current;
      // 1. 完全 keep 其他 option
      const exact = cands.find(v =>
        this.product.options.every((_, i) =>
          i === optionIdx || getOptionValue(v, i) === getOptionValue(cur, i)
        )
      );
      if(exact) return exact;
      // 2. 仅本 option 改,其他 option 退而求其次找 available
      const available = cands.find(v => v.available);
      return available || cands[0];
    }

    /* 检查 (固定本 option = value 时) 该组合是否在 SKU 里存在/有库存 */
    valueAvailability(optionIdx, value){
      // 在保持其他 option 当前值的前提下检查
      const cur = this.current;
      const v = this.product.variants.find(variant =>
        this.product.options.every((_, i) =>
          i === optionIdx ? getOptionValue(variant, i) === value
                          : getOptionValue(variant, i) === getOptionValue(cur, i)
        )
      );
      if(v) return { exists: true, available: v.available };
      // 不存在精确组合,检查该 value 在任何组合下是否存在
      const anyExists = this.product.variants.some(variant => getOptionValue(variant, optionIdx) === value);
      return { exists: false, available: false, anyExists };
    }

    /* 渲染入口 */
    render(){
      this.product.options.forEach((_, i) => this.renderOption(i));
      this.renderActiveName();
    }

    renderOption(optionIdx){
      const container = this.querySelector(`[data-vp2-picker][data-option-index="${optionIdx}"]`);
      if(!container) return;
      container.innerHTML = '';

      const opt = this.product.options[optionIdx];
      const values = opt.values || [];
      const isImage = this.imageMode[optionIdx];
      container.classList.toggle('is-image', isImage);
      container.classList.toggle('is-text', !isImage);

      // 全部展示,不收起
      const visible = values.slice();
      const hidden = 0;

      const curValue = getOptionValue(this.current, optionIdx);

      visible.forEach(val => {
        container.appendChild(this.buildItem(optionIdx, val, isImage, val === curValue));
      });

      if(hidden > 0){
        const expandBtn = document.createElement('button');
        expandBtn.type = 'button';
        expandBtn.className = isImage
          ? 'vp2-picker-item vp2-picker-item-image vp2-picker-expand'
          : 'vp2-picker-item vp2-picker-item-text vp2-picker-expand';
        expandBtn.innerHTML = `<span class="vp2-expand-text">${(this.dataset.moreOptions || '他の__OPTION__を見る').replace('__OPTION__', opt.name)} <span class="vp2-expand-count">(${hidden})</span></span>`;
        expandBtn.onclick = () => {
          this.expanded[optionIdx] = true;
          this.renderOption(optionIdx);
        };
        container.appendChild(expandBtn);
      }
    }

    buildItem(optionIdx, value, isImage, isActive){
      const av = this.valueAvailability(optionIdx, value);
      const exists = av.exists || av.anyExists;
      const inStock = av.available;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = (isImage ? 'vp2-picker-item vp2-picker-item-image' : 'vp2-picker-item vp2-picker-item-text')
                    + (isActive ? ' active' : '')
                    + (!exists ? ' unavailable' : '')
                    + (exists && !inStock ? ' oos' : '');
      btn.disabled = !exists;

      if(isImage){
        // 找该 option=value 的代表 variant 拿图
        const v = this.product.variants.find(x => getOptionValue(x, optionIdx) === value && x.thumb)
              ||  this.product.variants.find(x => getOptionValue(x, optionIdx) === value);
        const img = v && v.thumb ? imgWidth(v.thumb, 180) : '';
        btn.innerHTML = `
          <span class="vp2-item-img">${img ? `<img src="${img}" alt="${value}" loading="lazy">` : ''}</span>
          <span class="vp2-item-name">${value}</span>`;
      } else {
        btn.textContent = value;
      }

      // OOS:不加红色徽标,仅靠灰显 + 删除线表达
      // hover 时浏览器原生 title 提示
      if(exists && !inStock){
        btn.title = (this.dataset.soldOut || '__VALUE__ は在庫切れです').replace('__VALUE__', value);
      }

      if(exists){
        btn.onclick = () => this.pick(optionIdx, value);
      }
      return btn;
    }

    pick(optionIdx, value){
      const v = this.findBestVariant(optionIdx, value);
      if(!v) return;
      this.changeTo(v);
      // localStorage:仅记忆"非图片模式"option(通常是机型/尺寸,跨产品复用),图片模式跨产品意义不大
      if(!this.imageMode[optionIdx]){
        try {
          const last = JSON.parse(localStorage.getItem(LS_LAST) || '{}');
          last[this.product.options[optionIdx].name] = value;
          localStorage.setItem(LS_LAST, JSON.stringify(last));
        } catch(e){}
      }
    }

    changeTo(variant){
      this.current = variant;
      this.render();
      const inp = this.querySelector('[data-vp2-variant-id]');
      if(inp) inp.value = variant.id;
      // 同步主题原生 <variant-selects> 触发主题理解的事件
      this.syncNativePicker(variant);
      // URL
      const u = new URL(location.href);
      u.searchParams.set('variant', variant.id);
      history.replaceState({}, '', u);
      this.dispatchVariantChange();
    }

    syncNativePicker(variant){
      const native = document.querySelector('variant-selects, variant-radios');
      if(!native) return;
      const opts = [variant.option1, variant.option2, variant.option3];
      let lastSelect = null;
      opts.forEach((val, i) => {
        if(!val) return;
        const sels = native.querySelectorAll('select');
        const sel = sels[i] || native.querySelector(`select[name="options[${this.product.options[i]?.name}]"]`);
        if(sel){ sel.value = val; lastSelect = sel; }
        const radios = native.querySelectorAll(`input[type="radio"][name*="${this.product.options[i]?.name||''}"]`);
        radios.forEach(r => { if(r.value === val) r.checked = true; });
      });
      if(lastSelect){
        lastSelect.dispatchEvent(new Event('input', { bubbles: true }));
        lastSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    renderActiveName(){
      const el = this.querySelector('[data-vp2-current] strong');
      if(!el) return;
      el.textContent = (this.current.option1 || '')
        + (this.current.option2 ? ' / ' + this.current.option2 : '')
        + (this.current.option3 ? ' / ' + this.current.option3 : '');
    }

    dispatchVariantChange(){
      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: this.current, product: this.product }
      }));
    }
  }

  if(!customElements.get('variant-picker-v2')){
    customElements.define('variant-picker-v2', VariantPickerV2);
  }

  /* ── 主图同步 ─────────────────────────────────────── */
  class ProductMediaVp2 extends HTMLElement {
    connectedCallback(){
      this.mainEl = this.querySelector('[data-vp2-main] img');
      document.addEventListener('variant:change', e => this.update(e.detail));
    }
    update({variant}){
      if(this.mainEl && variant.featured_image){
        this.mainEl.src = imgWidth(variant.featured_image, 1024);
      }
    }
  }
  if(!customElements.get('product-media-vp2')){
    customElements.define('product-media-vp2', ProductMediaVp2);
  }

  /* 注:移动端 sticky CTA 委托给 Vision 主题原生
     (内置 Shopify Dynamic Checkout Button:Apple Pay / PayPal / 通用購入 自动切换) */
})();
